import React, { useCallback, useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Stack,
  List,
  ListItem,
  ListItemText,
  IconButton,
  TextField,
  Chip,
  Box,
  Alert,
  Divider,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  Add as AddIcon,
  Star as StarIcon,
} from '@mui/icons-material';
import ExercisePickerDialog from '../exercises/ExercisePickerDialog';
import { ExerciseGroupService, type ExerciseGroup, type ExerciseGroupExercise } from '../../services/exerciseGroupService';
import type { Exercise } from '../../services/types';

interface ExerciseGroupManagerDialogProps {
  open: boolean;
  onClose: () => void;
  groups: ExerciseGroup[];
  /** Called after any create/update/delete so the parent can refresh its list. */
  onChanged: () => void;
}

interface DraftGroup {
  id: number | null;
  name: string;
  exercises: ExerciseGroupExercise[];
}

const emptyDraft = (): DraftGroup => ({ id: null, name: '', exercises: [] });

/**
 * Create/rename/delete named exercise groups and manage their exercises. Adding
 * exercises reuses the shared single-select ExercisePickerDialog (one at a time).
 */
const ExerciseGroupManagerDialog: React.FC<ExerciseGroupManagerDialogProps> = ({
  open,
  onClose,
  groups,
  onChanged,
}) => {
  const [draft, setDraft] = useState<DraftGroup | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      setDraft(null);
      setError(null);
      setPickerOpen(false);
    }
  }, [open]);

  const startNew = () => {
    setError(null);
    setDraft(emptyDraft());
  };

  const startEdit = (group: ExerciseGroup) => {
    setError(null);
    setDraft({ id: group.id, name: group.name, exercises: [...group.exercises] });
  };

  const handleAddExercise = (exercise: Exercise) => {
    setPickerOpen(false);
    setDraft((prev) => {
      if (!prev) return prev;
      if (prev.exercises.some((e) => e.id === exercise.id)) return prev;
      return {
        ...prev,
        exercises: [...prev.exercises, { id: exercise.id, name: exercise.name, variationName: exercise.variationName ?? null }],
      };
    });
  };

  const handleRemoveExercise = (exerciseId: number) => {
    setDraft((prev) => (prev ? { ...prev, exercises: prev.exercises.filter((e) => e.id !== exerciseId) } : prev));
  };

  const handleSave = async () => {
    if (!draft) return;
    if (draft.name.trim() === '') {
      setError('Въведи име на групата.');
      return;
    }
    if (draft.exercises.length < 2) {
      setError('Групата трябва да съдържа поне 2 упражнения.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const exerciseIds = draft.exercises.map((e) => e.id);
      if (draft.id === null) {
        await ExerciseGroupService.createGroup({ name: draft.name.trim(), exerciseIds });
      } else {
        await ExerciseGroupService.updateGroup(draft.id, { name: draft.name.trim(), exerciseIds });
      }
      setDraft(null);
      onChanged();
    } catch (err: any) {
      setError(err?.message || 'Неуспешно запазване на групата.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = useCallback(async (group: ExerciseGroup) => {
    if (!window.confirm(`Да изтрия групата "${group.name}"?`)) return;
    setSaving(true);
    setError(null);
    try {
      await ExerciseGroupService.deleteGroup(group.id);
      onChanged();
    } catch (err: any) {
      setError(err?.message || 'Неуспешно изтриване на групата.');
    } finally {
      setSaving(false);
    }
  }, [onChanged]);

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Stack direction="row" spacing={1} alignItems="center">
            <StarIcon sx={{ color: '#e91e63' }} />
            <span>Групи упражнения</span>
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          {draft === null ? (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Създай група от няколко упражнения и виж анализ за тях заедно.
              </Typography>
              {groups.length === 0 ? (
                <Box sx={{ py: 2, textAlign: 'center', color: 'text.disabled' }}>
                  <Typography variant="body2">Все още няма създадени групи.</Typography>
                </Box>
              ) : (
                <List dense>
                  {groups.map((group) => (
                    <ListItem
                      key={group.id}
                      secondaryAction={
                        <Stack direction="row" spacing={0.5}>
                          <IconButton edge="end" onClick={() => startEdit(group)} disabled={saving} title="Редактирай">
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton edge="end" color="error" onClick={() => handleDelete(group)} disabled={saving} title="Изтрий">
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Stack>
                      }
                    >
                      <ListItemText
                        primary={group.name}
                        secondary={`${group.exercises.length} упражнения: ${group.exercises.map((e) => e.variationName || e.name).join(', ')}`}
                      />
                    </ListItem>
                  ))}
                </List>
              )}
              <Button startIcon={<AddIcon />} variant="outlined" onClick={startNew} sx={{ mt: 2 }} disabled={saving}>
                Нова група
              </Button>
            </>
          ) : (
            <>
              <TextField
                label="Име на групата"
                fullWidth
                value={draft.name}
                onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                sx={{ mb: 2 }}
                autoFocus
              />
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Упражнения ({draft.exercises.length})
              </Typography>
              {draft.exercises.length === 0 ? (
                <Typography variant="body2" color="text.disabled" sx={{ mb: 1 }}>
                  Добави поне 2 упражнения.
                </Typography>
              ) : (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
                  {draft.exercises.map((ex) => (
                    <Chip
                      key={ex.id}
                      label={ex.variationName || ex.name}
                      onDelete={() => handleRemoveExercise(ex.id)}
                      size="small"
                    />
                  ))}
                </Box>
              )}
              <Button startIcon={<AddIcon />} size="small" onClick={() => setPickerOpen(true)} disabled={saving}>
                Добави упражнение
              </Button>
              <Divider sx={{ my: 2 }} />
              <Stack direction="row" spacing={1} justifyContent="flex-end">
                <Button onClick={() => { setDraft(null); setError(null); }} disabled={saving}>
                  Отказ
                </Button>
                <Button variant="contained" onClick={handleSave} disabled={saving}>
                  {draft.id === null ? 'Създай' : 'Запази'}
                </Button>
              </Stack>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Затвори</Button>
        </DialogActions>
      </Dialog>

      <ExercisePickerDialog
        open={pickerOpen}
        title="Добави упражнение в групата"
        description="Търси упражнение, което да добавиш."
        confirmLabel="Добави"
        onConfirm={handleAddExercise}
        onClose={() => setPickerOpen(false)}
      />
    </>
  );
};

export default ExerciseGroupManagerDialog;
